import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { withEntities } from '@ngrx/signals/entities';
import { ethers } from 'ethers';
import { assetContracts, contractABI } from 'src/environments/environment';

import { Asset } from '../components/models/asset.model';
import { EIP6963ProviderDetail } from '../types/eip-6369';
import { openLinkInANewTab } from '../utils/open-in-a-new-tab.utils';
import { TranslateService } from '@ngx-translate/core';
import { ToastrFactoryService } from '../services/toastr-factory.service';
import { inject } from '@angular/core';

interface PaymentDetails {
  payingAsset?: {
    id?: string;
    assetTicker?: string;
    assetChain?: string;
  };
  quote?: {
    price?: number;
  };
  selectedReceiverWallet?: {
    cryptoAddress?: {
      address?: string | null;
    };
  };
}

type BroserWalletState = {
  browserWalletsObject: EIP6963ProviderDetail[];
  hasMetamask: boolean;
};

const initialState: BroserWalletState = {
  browserWalletsObject: [],
  hasMetamask: false,
};

export const WalletBrowserExtensionStore = signalStore(
  {
    providedIn: 'root',
  },
  withEntities<Asset>(),
  withState(initialState),
  withMethods((store, translate = inject(TranslateService), toastrFactory = inject(ToastrFactoryService)) => {
    const getNoTypeWindow = () => window as any;

    const assetToWei = (paymentValue: number, decimals: number): string => {
      const contractDecimals = 10 ** decimals;
      const weiValue = BigInt(Math.round(paymentValue * contractDecimals));
      return '0x' + weiValue.toString(16);
    };

    const getMetaMaskProvider = async (wallet: EIP6963ProviderDetail) => {
      if (!wallet) throw new Error(`No wallet found!`);
      return new ethers.BrowserProvider(wallet.provider);
    };

    const transferToken = async (
      wallet: EIP6963ProviderDetail,
      toAddress: string,
      contractAddress: string,
      amount: number,
      decimals: number,
    ) => {
      const provider = await getMetaMaskProvider(wallet);
      const signer = await provider.getSigner();
      const contract = new ethers.Contract(contractAddress, contractABI, signer);
      ethers.getAddress(toAddress);
      const tx = await contract['transfer'](toAddress, assetToWei(amount, decimals));
      await tx.wait();
      return tx;
    };

    const handleWalletPayment = async (
      wallet: EIP6963ProviderDetail,
      paymentDetails: PaymentDetails,
    ): Promise<{ transactionId: string; success: boolean }> => {
      if (!wallet) {
        openLinkInANewTab('https://metamask.io/download/');
        throw new Error('No wallet selected');
      }

      let accounts = (await wallet.provider.request({ method: 'eth_requestAccounts' })) as string[];
      const assetId = paymentDetails.payingAsset?.id;
      const selectedAssetChainId = assetContracts[assetId as keyof typeof assetContracts].chainId;
      let provider = wallet.provider;

      if (wallet.info.name === 'Exodus') {
        await getNoTypeWindow().exodus.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: selectedAssetChainId }],
        });

        accounts = (await getNoTypeWindow().exodus.ethereum.request({ method: 'eth_requestAccounts' })) as string[];
        provider = getNoTypeWindow().exodus.ethereum;
      }

      const walletChainId = await wallet.provider.request({ method: 'eth_chainId' });
      const payingAsset = paymentDetails.payingAsset?.assetTicker;

      if (walletChainId !== selectedAssetChainId) {
        throw new Error('INVALID_NETWORK');
      }

      if (payingAsset === 'ETH' || payingAsset === 'POL') {
        const txHash = await provider.request({
          method: 'eth_sendTransaction',
          params: [
            {
              from: accounts[0],
              to: paymentDetails.selectedReceiverWallet?.cryptoAddress?.address || '',
              value: assetToWei(paymentDetails.quote?.price || 0, 18),
            },
          ],
        });

        return { transactionId: txHash as string, success: true };
      }

      const contract = assetContracts[assetId as keyof typeof assetContracts];
      if (!contract?.address) {
        throw new Error('Invalid contract address');
      }
      const walletAddress = paymentDetails.selectedReceiverWallet?.cryptoAddress?.address || '';

      const response = await transferToken(
        wallet,
        walletAddress,
        contract.address,
        paymentDetails.quote?.price || 0,
        contract.decimals,
      );

      return { transactionId: response.hash, success: true };
    };

    const checkBrowserWallets = () => {
      window.addEventListener('eip6963:announceProvider', (event: any) => {
        const walletProvider = event.detail as EIP6963ProviderDetail;

        if (
          walletProvider.info.name === 'MetaMask' ||
          walletProvider.info.name === 'Trust Wallet' ||
          walletProvider.info.name === 'Coinbase Wallet'
        ) {
          if (walletProvider.info.name === 'MetaMask') {
            patchState(store, {
              hasMetamask: true,
            });
          }

          patchState(store, {
            browserWalletsObject: [...store.browserWalletsObject(), walletProvider],
          });
        }
      });

      window.dispatchEvent(new Event('eip6963:requestProvider'));
    };

    const getMetamaskProvider = () => {
      return store.browserWalletsObject().find((wallet) => wallet.info.name === 'MetaMask');
    };

    const getMetamaskAddress = async () => {
      if (!store.hasMetamask) {
        openLinkInANewTab('https://metamask.io/download/');
      }

      try {
        const addresses: string[] = (await getMetamaskProvider()?.provider.request({
          method: 'eth_requestAccounts',
        })) as string[];
        return addresses[0];
      } catch (e: any) {
        if (e.message?.includes('Already processing')) {
          toastrFactory.error(translate.instant('youHaveToAcceptnickyAccessRequestInMetamask'));
        }
        console.log(e);
        return undefined;
      }
    };

    return {
      getMetamaskAddress,
      checkBrowserWallets,
      handleWalletPayment,
    };
  }),

  withHooks((store) => ({
    onInit: () => {
      store.checkBrowserWallets();
    },
  })),
);
